import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button } from '@panwds/react-ui'
import UTSGNew from './UTSGNew'
import UTSGTreeDropdown from './UTSGTreeDropdown'
import UAccordionHeader from './UAccordionHeader'
import { Actions, updateStateProps } from '../../actions'
import { TSG_FIELD_NAME } from '../common/GlobalConstants'

import type { VFC } from 'react'
import type { CommonStoreState } from '../../store'
import type { BannerProps } from './UAccordion'
import { ifIpRestricted } from '../../../common/context/IpRestrictContext'
import { ERROR_API_IP_RESTRICTED_TSG } from '../../../common/constants/GlobalConstants'
type UTSGDataType = {
  title?: string
  subtitle?: string
  state: CommonStoreState
  actions: Actions
  updateTitle: (title: string) => void
  setAccordionOpen: (isOpen: boolean) => void
  setDisable: (disable: boolean) => void
  setEditDisable: (disable: boolean) => void
  setBanner: (banner: BannerProps) => void
}

const defaultProps = {
  title: 'Specify the Recipient',
  subtitle: 'Use or rename the tenant that we created or add a new subtenant as the recipient.',
}

const UTSGComponent: VFC<UTSGDataType> = (props) => {
  const { title, subtitle, state, updateTitle, setAccordionOpen, setDisable, setEditDisable, setBanner } = props
  const { lockedInfo, instances } = state
  const { setValue, register, setError, clearErrors } = useFormContext()
  const [selectedTsgId, setSelectedTsgId] = useState<string | undefined>()
  const [recipientName, setRecipientName] = useState<string>('')
  const isDayZeroUser = useMemo(() => {
    return !Boolean(props.state?.tsgs?.children?.length)
  }, [props.state?.tsgs?.children?.length])

  register(TSG_FIELD_NAME)
  useEffect(() => {
    if (instances.error) {
      // error on fetching instances, need to setError
      let message: string
      if (ifIpRestricted(instances.error)) {
        message = ERROR_API_IP_RESTRICTED_TSG
      }
      else {
        message = instances.error.message ?? String(instances.error)
      }
      setBanner({
        message: (
          <>
            {message}
            <br />
            Please try another Tenant.
          </>
        ),
        display: true,
        appearance: 'error',
      })
      setError(TSG_FIELD_NAME, instances.error)
    }
    else {
      setBanner({
        display: false,
      })
      clearErrors(TSG_FIELD_NAME)
    }
  }, [clearErrors, instances.error, setBanner, setError])

  useEffect(() => {
    if (!selectedTsgId) {
      if (isDayZeroUser) {
        updateTitle(`Recipient: ${recipientName}`)
      }
      return
    }
    const selectedTsg = state.tsgs.get(selectedTsgId)
    updateStateProps({ tsg: selectedTsg })
    updateTitle(`Recipient: ${selectedTsg?.display_name}`)
    setValue(TSG_FIELD_NAME, {
      id: selectedTsgId,
      value: selectedTsg?.display_name
    })
  }, [isDayZeroUser, recipientName, selectedTsgId, setValue, state.tsgs, updateTitle])

  const tsgDisabled = useMemo(() => {
    // disable only if set by lockedInfo, if user only have 1 tsg, don't disable and let user choose
    return Boolean(lockedInfo?.tsg_ids?.length === 1)
  }, [lockedInfo?.tsg_ids?.length])

  useEffect(() => {
    if (tsgDisabled && lockedInfo?.tsg_ids?.length === 1) {
      setSelectedTsgId(lockedInfo.tsg_ids[0])
      setEditDisable(true)
      setAccordionOpen(false)
    }
    else {
      setEditDisable(false)
    }
  }, [lockedInfo, setAccordionOpen, setEditDisable, tsgDisabled])

  useEffect(() => {
    if (state.supportAccountId) {
      setDisable(false)
    }
    else {
      setDisable(true)
    }
  }, [setDisable, state.supportAccountId])

  return (
    <div>
      <UAccordionHeader title={title || defaultProps.title} subtitle={subtitle || defaultProps.subtitle} />
      <div className='cm-mt-4 cm-w-full'>
        {isDayZeroUser ?
          <UTSGNew {...props} setRecipientName={setRecipientName} /> :
          <UTSGTreeDropdown {...props} selectedTsgId={selectedTsgId} onChangeCallback={(tsgId) => {
            setSelectedTsgId(tsgId)
            setAccordionOpen(false)
          }} />
        }
      </div>
      {isDayZeroUser && <Button
        addClassName='cm-absolute cm-right-0 cm-top-0'
        appearance='primary'
        onClick={() => setAccordionOpen(false)}
      >Done</Button>}
    </div>
  )
}

export default UTSGComponent
